import { Component, ElementRef, ViewChild } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { AppSidebarComponent } from "./app.sidebar.component";
import { LayoutService } from "./service/app.layout.service";
import { AuthService } from "../core/auth/services/auth.service";
import { Observable } from "rxjs";
import { filter, map, startWith } from "rxjs/operators";
import { User } from "../core/auth/utils/user.models";
import { ThemeService } from "../core/services/theme.service";
import { routes } from "../../environments/environment.routes";
import {
  AlertHistorySessionService,
  AlertHistoryUser,
} from "../features/alerts/services/alert-history-session.service";

@Component({
  selector: "twx-topbar",
  templateUrl: "./app.topbar.component.html",
  styleUrls: ["./app.topbar.component.scss"],
  standalone: false,
})
export class AppTopbarComponent {
  @ViewChild("menubutton") menuButton!: ElementRef;

  @ViewChild(AppSidebarComponent) appSidebar!: AppSidebarComponent;
  user$: Observable<User | undefined>;

  /** The Store Alert Feed shows "Back to Stores" in the topbar (per design). */
  showBackToStores$: Observable<boolean>;

  /**
   * Alert History screens show "Back to GlobalWorx" (design update
   * 2026-08-27): users arrive from the GlobalWorx app in the same tab. Shown
   * for any session type, so local runs and the client-preview build match
   * the design.
   *
   * The Store Alert Feed is the exception: it offers "Back to Stores", and
   * two competing back actions in one bar read as a single stack the user can
   * walk up, which they are not. The feed's back action goes up one level, so
   * it is the only one shown; GlobalWorx is one tap further, from Your Stores.
   */
  showBackToGlobalWorx$: Observable<boolean>;

  /**
   * "Back to GlobalWorx" destination, opened in the same tab: the Mobility
   * home on the GWX origin the redirect entry's api_url named (LPG-3096).
   */
  get globalWorxHomeUrl(): string {
    return this.alertHistorySession.homeUrl;
  }

  constructor(
    public layoutService: LayoutService,
    public el: ElementRef,
    public theme: ThemeService,
    private auth: AuthService,
    private router: Router,
    private alertHistorySession: AlertHistorySessionService,
  ) {
    this.user$ = this.auth.user$;
    this.showBackToStores$ = this.routeFlag$(() => this.isStoreAlertFeed());
    this.showBackToGlobalWorx$ = this.routeFlag$(
      () => this.isAlertHistoryRoute() && !this.isStoreAlertFeed(),
    );
  }

  /** Re-evaluates a URL predicate after every navigation, seeded with the current URL. */
  private routeFlag$(predicate: () => boolean): Observable<boolean> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(predicate),
      startWith(predicate()),
    );
  }

  private isAlertHistoryRoute(): boolean {
    const base = `/${routes.frontend.alerts}`;
    const url = this.router.url;
    return (
      url === base || url.startsWith(`${base}/`) || url.startsWith(`${base}?`)
    );
  }

  private isStoreAlertFeed(): boolean {
    return this.router.url.startsWith(
      `/${routes.frontend.alerts}/${routes.frontend.alertsStore}/`,
    );
  }

  backToStores(): void {
    this.router.navigate(["/", routes.frontend.alerts]);
  }

  /** User info from the Alert History entry token (TWX-536), when present. */
  get alertHistoryUser(): AlertHistoryUser | null {
    return this.alertHistorySession.user;
  }

  onMenuButtonClick() {
    this.layoutService.onMenuToggle();
  }

  onProfileButtonClick() {
    this.layoutService.showRightMenu();
  }

  onSearchClick() {
    this.layoutService.toggleSearchBar();
  }

  onRightMenuClick() {
    this.layoutService.showRightMenu();
  }

  anchor() {
    this.layoutService.state.anchored = !this.layoutService.state.anchored;
  }
}
